import React, { useState } from 'react';
import { MailIcon, CalendarIcon, MapPinIcon, SendIcon, ExternalLinkIcon } from 'lucide-react';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Input, TextArea } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { useLanguage } from '../context/LanguageContext';
// ============================================================
// CALENDLY: Replace the placeholder below with your actual link
// Example: 'https://calendly.com/helgi/discovery'
// Once you have it, change CALENDLY_ENABLED to true as well.
// ============================================================
const CALENDLY_URL = 'https://calendly.com/YOUR-LINK-HERE';
const CALENDLY_ENABLED = false; // ← flip to true once you have the link
// ============================================================
// FORM EMAIL: Submissions go to this address via formsubmit.co
// First submission triggers a confirmation email from FormSubmit.
// Click the link in that email to activate. After that, all
// form submissions arrive directly in this inbox.
// ============================================================
const FORM_EMAIL = 'helgi@helgig.com';
export function Contact() {
  const {
    t,
    language
  } = useLanguage();
  const isIS = language === 'is';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    interest: '',
    message: ''
  });
  // Updated interest options matching current challenge areas
  const interestOptions = isIS ? ['Nýr stjórnandi', 'Ágreiningar og krefjandi samtöl', 'Betri fundir og vinnustofur', 'Að leiða í flóknum aðstæðum', 'Einstaklingsþjálfun', 'Innbyggður sérfræðingur', 'Annað'] : ['New to leadership', 'Conflict and difficult conversations', 'Better meetings and workshops', 'Leading in complexity', '1:1 Coaching', 'Embedded specialist', 'Other'];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const response = await fetch(`https://formsubmit.co/ajax/${FORM_EMAIL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          interest: formData.interest || interestOptions[0],
          message: formData.message,
          _subject: `New inquiry: ${formData.interest || interestOptions[0]}`
        })
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          interest: '',
          message: ''
        });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <PageTransition data-id="element-278" data-mp="313enw3b8o8zp">
      <div className="pt-12 pb-24 relative overflow-hidden" data-id="element-279" data-mp="3146hx9p6tynv">
        <DecorativeShape type="blob" color="bg-coral/5" className="top-0 left-0 w-[800px] h-[800px] -translate-x-1/2 -translate-y-1/2 -z-10" data-id="element-280" data-mp="3559yjyf27yq5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-281" data-mp="1tqmnteuk5s8n">
          <Animate className="text-center max-w-2xl mx-auto mb-16" data-id="element-282" data-mp="1toryloc97dyr">
            <h1 className="text-5xl font-bold mb-6" data-id="element-283" data-mp="29yst5lrqbji2">
              {isIS ? 'Við skulum tala saman' : "Let's talk"}
            </h1>
            <p className="text-xl text-gray-600" data-id="element-284" data-mp="2557stn2nydfp">
              {isIS ? 'Fylltu út eyðublaðið eða bókaðu tíma beint í dagatalinu mínu. Enginn þrýstingur - bara heiðarleg samtal.' : 'Fill out the form or book a time directly in my calendar. No pitch - just an honest conversation.'}
            </p>
          </Animate>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-285" data-mp="2nxiq91ejtj6f">
            {/* Left column: info cards */}
            <div className="space-y-6" data-id="element-286" data-mp="2ix4f3ahnht46">
              {/* Contact info card */}
              <Animate delay={0.1} data-id="element-287" data-mp="2ew2f2rfwtd15">
                <Card className="p-8 bg-teal text-white relative overflow-hidden" data-id="element-288" data-mp="2ez6qa8rsl91b">
                  <DecorativeShape type="circle" color="bg-white/10" className="bottom-0 right-0 w-48 h-48 translate-x-1/4 translate-y-1/4" data-id="element-289" data-mp="17l3mfkho1kj5" />
                  <h3 className="text-2xl font-bold mb-6 text-[#2D3748]" data-id="element-290" data-mp="3f37kdn65uzlx">
                    {isIS ? 'Tengiliðaupplýsingar' : 'Contact Information'}
                  </h3>
                  <div className="space-y-5" data-id="element-291" data-mp="3o4ffxxhalmcb">
                    <div className="flex items-center gap-4" data-id="element-292" data-mp="3swp3x7yukcby">
                      <div className="p-3 bg-white/20 rounded-full flex-shrink-0" data-id="element-293" data-mp="3sy046xt331yb">
                        <MailIcon size={22} data-id="element-294" data-mp="1pmd4hmx8829" />
                      </div>
                      <div data-id="element-295" data-mp="6ophdzv2ua96">
                        <p className="text-sm mb-0.5 text-[#2D3748]" data-id="element-296" data-mp="6nxj2ha3hyf7">
                          {isIS ? 'Sendu mér tölvupóst' : 'Send me an email'}
                        </p>
                        <a href={`mailto:${FORM_EMAIL}`} className="font-bold text-lg hover:underline text-[#2D3748]" data-id="element-297" data-mp="fqf4ithxen15">
                          {FORM_EMAIL}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-298" data-mp="3kt8vmegxwkxh">
                      <div className="p-3 bg-white/20 rounded-full flex-shrink-0" data-id="element-299" data-mp="3ftmj678xr8pq">
                        <MapPinIcon size={22} data-id="element-300" data-mp="3tt4buxpcr64d" />
                      </div>
                      <div data-id="element-301" data-mp="3tr1a5geu7ojt">
                        <p className="text-sm mb-0.5 text-[#2D3748]" data-id="element-302" data-mp="3ouav8werqyzz">
                          {isIS ? 'Staðsetning' : 'Location'}
                        </p>
                        <p className="font-bold text-lg text-[#2D3748]" data-id="element-303" data-mp="7k31slndqp2z">
                          {isIS ? 'Reykjavík / Um allan heim' : 'Reykjavík / Worldwide'}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </Animate>

              {/* Calendly / book a call card */}
              <Animate delay={0.2} data-id="element-304" data-mp="qaawcfd0k77k">
                <Card className="p-8 border-2 border-gray-100" data-id="element-305" data-mp="lduajrtae4xn">
                  <div className="flex items-center gap-4 mb-4" data-id="element-306" data-mp="lbhjmp5jwk8g">
                    <div className="p-3 bg-coral/10 text-coral rounded-full flex-shrink-0" data-id="element-307" data-mp="14zni2gyq89ss">
                      <CalendarIcon size={22} data-id="element-308" data-mp="19ww3qrj32vys" />
                    </div>
                    <h3 className="text-xl font-bold text-charcoal" data-id="element-309" data-mp="v1g6kdn7ijy6">
                      {isIS ? 'Bóka uppgötvunarspjall' : 'Book a discovery call'}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-310" data-mp="m7biu33ddomv">
                    {isIS ? '15-20 mínútur þar sem við skoðum hvað þú ert að glíma við og hvort við náum tengingu. Ókeypis og án skuldbindingar.' : '15-20 minutes to look at what you are dealing with and see if there is a fit. Free and without commitment.'}
                  </p>

                  {CALENDLY_ENABLED ? <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="w-full" data-id="element-311" data-mp="7b40777m0v9p">
                      <Button variant="primary" className="w-full justify-center" rightIcon={<ExternalLinkIcon size={16} data-id="element-313" data-mp="dqubvjk4g9ie" />} data-id="element-312" data-mp="ir8icx62rbeb">
                        {isIS ? 'Velja tíma í dagatalinu' : 'Pick a time in my calendar'}
                      </Button>
                    </a> : <div data-id="element-314" data-mp="3w5dbfui35bha">
                      <Button variant="outline" className="w-full justify-center opacity-60 cursor-not-allowed" disabled data-id="element-315" data-mp="3w4tvgjbzwx5w">
                        {isIS ? 'Calendly er að koma bráðum' : 'Calendly coming soon'}
                      </Button>
                      <p className="text-xs text-gray-400 mt-2 text-center" data-id="element-316" data-mp="3d9wxvqfjue6z">
                        {isIS ? 'Á meðan - sendu mér tölvupóst eða fylltu út eyðublaðið.' : 'In the meantime - email me or fill out the form.'}
                      </p>
                    </div>}
                </Card>
              </Animate>
            </div>

            {/* Right column: contact form */}
            <Animate delay={0.15} data-id="element-317" data-mp="192tv9ggz0hf3">
              <Card className="p-8 md:p-10" data-id="element-318" data-mp="193loikn3i327">
                {isSubmitted ? <div className="text-center py-12" data-id="element-319" data-mp="1e1ew9i41r54l">
                    <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-320" data-mp="1j08ajcr5r05t">
                      <SendIcon size={32} data-id="element-321" data-mp="1nrgmaffymxm5" />
                    </div>
                    <h3 className="text-2xl font-bold text-charcoal mb-2" data-id="element-322" data-mp="qbsxuwvi0txn">
                      {isIS ? 'Skilaboð send!' : 'Message sent!'}
                    </h3>
                    <p className="text-gray-600" data-id="element-323" data-mp="v817xvuu9wxy">
                      {isIS ? 'Takk fyrir að hafa samband. Ég kem til baka til þín innan 24 klukkustunda.' : "Thanks for reaching out. I'll get back to you within 24 hours."}
                    </p>
                    <Button variant="outline" className="mt-8" onClick={() => setIsSubmitted(false)} data-id="element-324" data-mp="ytp64qd7kdju">
                      {isIS ? 'Senda önnur skilaboð' : 'Send another message'}
                    </Button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-325" data-mp="g50l84eq6882">
                    <h3 className="text-2xl font-bold text-charcoal mb-2" data-id="element-326" data-mp="kw0jbczkywc5">
                      {isIS ? 'Senda skilaboð' : 'Send a message'}
                    </h3>

                    {submitError && <div className="p-4 bg-red-50 text-red-700 rounded-xl text-sm border border-red-100" data-id="element-327" data-mp="3oku24kcyxlpn">
                        {isIS ? 'Eitthvað fór úrskeiðis. Reyndu aftur eða sendu beint á ' : 'Something went wrong. Please try again or email directly at '}
                        <a href={`mailto:${FORM_EMAIL}`} className="underline font-bold" data-id="element-328" data-mp="1wohyz90sihl3">
                          {FORM_EMAIL}
                        </a>
                      </div>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-329" data-mp="1i0n35jrhjx3s">
                      <Input label={isIS ? 'Fornafn' : 'First name'} name="firstName" placeholder={isIS ? 'Jón' : 'Jane'} value={formData.firstName} onChange={handleChange} required data-id="element-330" data-mp="1hyk5cdk3czoi" />
                      <Input label={isIS ? 'Eftirnafn' : 'Last name'} name="lastName" placeholder={isIS ? 'Jónsson' : 'Smith'} value={formData.lastName} onChange={handleChange} required data-id="element-331" data-mp="z2kbex7jzm2a" />
                    </div>

                    <Input label={isIS ? 'Netfang' : 'Email'} name="email" type="email" placeholder={isIS ? 'jon@example.com' : 'jane@example.com'} value={formData.email} onChange={handleChange} required data-id="element-332" data-mp="keaij92q3vzm" />

                    {/* Interest dropdown — matches current challenge areas */}
                    <div data-id="element-333" data-mp="1r30kink1n8qu">
                      <label className="block text-sm font-bold text-charcoal mb-2 ml-1" data-id="element-334" data-mp="1r1pk8xpt4j4h">
                        {isIS ? 'Mig langar að vita meira um...' : "I'd like to know more about..."}
                      </label>
                      <select name="interest" value={formData.interest} onChange={handleChange} className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 focus:border-teal focus:ring-0 transition-colors outline-none bg-white text-charcoal appearance-none cursor-pointer" style={{
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1rem center'
                  }} data-id="element-335" data-mp="3bm30fuavh2ke">
                        <option value="" disabled data-id="element-336" data-mp="2nk97syov5tox">
                          {isIS ? '— Veldu viðfangsefni —' : '— Select a topic —'}
                        </option>
                        {interestOptions.map(option => <option key={option} value={option} data-id="element-337" data-mp="31j8rdrxirie8">
                            {option}
                          </option>)}
                      </select>
                    </div>

                    <TextArea label={isIS ? 'Skilaboð' : 'Message'} name="message" rows={4} placeholder={isIS ? 'Segðu mér aðeins frá stöðunni þinni eða áskoruninni sem þú stendur frammi fyrir...' : "Tell me a bit about the situation or challenge you're facing..."} value={formData.message} onChange={handleChange} required data-id="element-338" data-mp="3besnoffzwj3i" />

                    <Button type="submit" className="w-full justify-center" size="lg" isLoading={isSubmitting} rightIcon={<SendIcon size={18} data-id="element-340" data-mp="3qddc1wie2gai" />} data-id="element-339" data-mp="359lidh91klx">
                      {isIS ? 'Senda skilaboð' : 'Send message'}
                    </Button>
                  </form>}
              </Card>
            </Animate>
          </div>
        </div>
      </div>
    </PageTransition>;
}